import { initConfig, getConfig, isInitialized, SdkConfig } from './config';
import { sendHit, HitOptions } from './hits-emitter';
import {
  setHitsContext,
  getHitsContext,
  clearHitsContext,
  HitsContext,
} from './hits-context';
import * as snowplowFacade from './snowplow-facade';

export type InitConfig = SdkConfig;

export const version = '0.1.1';

export function init(cfg: InitConfig): void {
  initConfig(cfg);
}

export function hit(
  name: string,
  eventType: string,
  data?: Record<string, unknown>,
  options?: HitOptions,
): void {
  if (typeof name !== 'string' || !name) {
    throw new Error('gokwik.hit: name is required (non-empty string)');
  }
  if (typeof eventType !== 'string' || !eventType) {
    throw new Error('gokwik.hit: eventType is required (non-empty string)');
  }
  const cfg = getConfig();
  sendHit({ name, eventType, data }, cfg, options);
}

export const snowplow = snowplowFacade;

export { setHitsContext, getHitsContext, clearHitsContext, isInitialized };
export type { HitOptions, HitsContext, SdkConfig };

// ---------------------------------------------------------------------------
// Global install (idempotent) — see browser.ts for the entry that calls this.
// ---------------------------------------------------------------------------

/** The shape exposed as `window.gokwik`. */
export interface GokwikGlobal {
  init: typeof init;
  hit: typeof hit;
  snowplow: typeof snowplow;
  setHitsContext: typeof setHitsContext;
  getHitsContext: typeof getHitsContext;
  clearHitsContext: typeof clearHitsContext;
  isInitialized: typeof isInitialized;
  version: string;
}

/** Build the public API object attached to the global. */
export function buildGlobalApi(): GokwikGlobal {
  return {
    init,
    hit,
    snowplow,
    setHitsContext,
    getHitsContext,
    clearHitsContext,
    isInitialized,
    version,
  };
}

/**
 * Idempotently install the SDK on `target` (the global object).
 *
 * Each loaded bundle is its own IIFE with private module-scope state
 * (`_config`, sticky context). If two teams include the `<script>` twice, a
 * naive assignment would replace `window.gokwik` on the second load and wipe
 * the first team's `init()` — surfacing as "init already called / cannot
 * re-init" or "init must be called before any SDK function".
 *
 * Guard: if `target.gokwik` is already a *real* loaded GoKwik instance — a
 * string `version` AND callable `init`/`hit` — keep it and return it (the
 * second load is a no-op). Otherwise install `api`, overwriting anything that
 * merely squats the global without our API (a foreign stub, a partial object).
 * Duplicate `init({ same merchantId, host })` is already a no-op (see
 * config.ts), so both teams then share one initialized instance.
 *
 * @returns the effective instance (the pre-existing one, or `api`).
 */
export function installGlobal(
  target: { gokwik?: { version?: unknown; init?: unknown; hit?: unknown } },
  api: GokwikGlobal,
): GokwikGlobal {
  const existing = target.gokwik;
  if (
    existing &&
    typeof existing.version === 'string' &&
    typeof existing.init === 'function' &&
    typeof existing.hit === 'function'
  ) {
    return existing as GokwikGlobal;
  }
  target.gokwik = api;
  return api;
}
